import { useState } from 'react';
import { KeyRound, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import apiService from '../../services/api';

interface OrgOption {
  id: number;
  name: string;
}

interface UserOption {
  id: number;
  username: string;
  email: string;
}

interface Props {
  orgs: OrgOption[];
  users: UserOption[];
}

type ConfirmTarget = null | { type: 'user'; id: number; label: string } | { type: 'org'; id: number; label: string } | { type: 'all' };

function SessionRevocationCard({ orgs, users }: Props) {
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [confirm, setConfirm] = useState<ConfirmTarget>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const execute = async (url: string) => {
    setLoading(true);
    setResult(null);
    try {
      const res = await apiService.post<{ message: string }>(url, {});
      if (res.success) {
        setResult({ type: 'success', message: (res.data as any)?.message || 'Sessions revoked' });
      } else {
        setResult({ type: 'error', message: (res as any).error?.message || 'Failed' });
      }
    } catch (err: any) {
      setResult({ type: 'error', message: err?.response?.data?.error?.message || err?.message || 'Failed' });
    } finally {
      setLoading(false);
      setConfirm(null);
    }
  };

  const handleConfirm = () => {
    if (!confirm) return;
    if (confirm.type === 'user') execute(`/users/${confirm.id}/revoke-tokens`);
    else if (confirm.type === 'org') execute(`/users/revoke-sessions/org/${confirm.id}`);
    else execute('/users/revoke-sessions/all');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <KeyRound className="h-4 w-4" />
          Session Revocation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {result && (
          <div className={`p-3 rounded-lg border text-sm ${
            result.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {result.message}
          </div>
        )}

        {/* Confirmation overlay */}
        {confirm && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">
                  {confirm.type === 'all'
                    ? 'Revoke ALL sessions globally?'
                    : confirm.type === 'org'
                      ? `Revoke all sessions for ${confirm.label}?`
                      : `Revoke sessions for ${confirm.label}?`}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  Affected users will be signed out immediately and must log in again.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleConfirm}
                disabled={loading}
                className="border-red-300 text-red-700 hover:bg-red-100"
              >
                {loading ? 'Revoking...' : 'Confirm'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirm(null)} disabled={loading}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!confirm && (
          <>
            {/* Revoke by user */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">Revoke by user</label>
              <div className="flex gap-2">
                <select
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                  className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select user...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!selectedUser}
                  onClick={() => {
                    const u = users.find(u => u.id === Number(selectedUser));
                    if (u) setConfirm({ type: 'user', id: u.id, label: u.username });
                  }}
                  className="border-red-200 text-red-600 hover:bg-red-50 shrink-0"
                >
                  Revoke
                </Button>
              </div>
            </div>

            {/* Revoke by org */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700">Revoke by organisation</label>
              <div className="flex gap-2">
                <select
                  value={selectedOrg}
                  onChange={e => setSelectedOrg(e.target.value)}
                  className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select organisation...</option>
                  {orgs.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!selectedOrg}
                  onClick={() => {
                    const o = orgs.find(o => o.id === Number(selectedOrg));
                    if (o) setConfirm({ type: 'org', id: o.id, label: o.name });
                  }}
                  className="border-red-200 text-red-600 hover:bg-red-50 shrink-0"
                >
                  Revoke
                </Button>
              </div>
            </div>

            {/* Revoke all */}
            <div className="pt-2 border-t border-neutral-200">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirm({ type: 'all' })}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Revoke all sessions globally
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default SessionRevocationCard;
